import { parseCapabilityTags } from './format';

/**
 * IDs of the two agents that actually run (payment gate + real AI pipeline).
 * Everything else in the marketplace is a listed-but-dormant "coming soon" agent.
 */
export const FUNCTIONAL_AGENT_IDS = ['meetingNotes', 'codingAssistant'];

export function isFunctionalAgentId(agentId: string | undefined | null): boolean {
  return !!agentId && FUNCTIONAL_AGENT_IDS.includes(agentId);
}

/** Helper copy shown under the CTA on the agent profile page */
export function getAgentHelperText(agentId: string | undefined | null): string | null {
  if (agentId === 'meetingNotes') {
    return 'Upload an audio file (.mp3, .wav, .m4a, ≤10MB) and get clean notes back — transcribed via Deepgram.';
  }
  if (agentId === 'codingAssistant') {
    return 'Paste a snippet, an error message, or a question about your code.';
  }
  return null;
}

/**
 * Build a sensible "What you get" bullet list for an agent card.
 * Functional agents get concrete pipeline bullets; dummy agents get bullets
 * derived from their capability tags so the marketplace still feels populated.
 */
export function getWhatYouGet(agent: {
  id?: string;
  name: string;
  capabilityTags: string;
  isFunctional: boolean;
}): string[] {
  if (agent.id === 'meetingNotes') {
    return [
      'Upload a raw meeting recording — no manual note-taking',
      'Deepgram-powered transcription of rambling audio',
      'A clean written summary in plain language',
      'A distinct Action Items list with owners and due dates when mentioned',
    ];
  }
  if (agent.id === 'codingAssistant') {
    return [
      'Paste code, stack traces, or questions for instant review',
      'Clear, plain-language explanations — no jargon dumps',
      'Bug spotting and concrete fix suggestions',
      'Powered by Claude Sonnet — a senior-engineer level reviewer',
    ];
  }

  const tags = parseCapabilityTags(agent.capabilityTags);
  const bullets = tags.slice(0, 3).map((tag) => `Handles ${tag.toLowerCase()} tasks on request`);
  bullets.push(agent.isFunctional ? 'Pay only per successful call' : 'Coming online soon — listed for preview');
  return bullets.length > 1 ? bullets : ['General-purpose assistant for this agent\'s specialty', 'Coming online soon — listed for preview'];
}
